/**
 * Choix du thème.
 *
 * Mémorisé **par appareil** (`localStorage`), et volontairement pas dans le
 * foyer : c'est une préférence d'affichage, pas une donnée partagée. La
 * synchroniser signifierait qu'un des deux téléphones impose son réglage à
 * l'autre — et ferait entrer une donnée sans valeur métier dans le delta.
 *
 * Le thème est appliqué **avant le premier rendu** (cf. main.tsx) : sinon
 * l'application s'afficherait une fraction de seconde dans le thème du système
 * avant de basculer.
 */

export type ThemeChoice = 'auto' | 'light' | 'dark';

const KEY = 'todofamille.theme';

const listeners = new Set<() => void>();
let choice: ThemeChoice = read();

function read(): ThemeChoice {
  const stored = localStorage.getItem(KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'auto';
}

export function getTheme(): ThemeChoice {
  return choice;
}

export function setTheme(next: ThemeChoice): void {
  choice = next;

  if (next === 'auto') localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, next);

  applyTheme();
  for (const listener of listeners) listener();
}

export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Thème effectivement affiché, une fois « auto » résolu. */
export function resolvedTheme(): 'light' | 'dark' {
  if (choice !== 'auto') return choice;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Applique le thème au document.
 *
 * `data-theme` pilote la feuille de style. `color-scheme` est indispensable en
 * plus : sans lui, les contrôles natifs (curseur de saisie, barres de défilement,
 * autocomplétion) resteraient dans le thème du système et jureraient.
 */
export function applyTheme(): void {
  const root = document.documentElement;
  const resolved = resolvedTheme();

  if (choice === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);

  root.style.colorScheme = resolved;

  // Teinte de la barre système sur Android et de la barre d'état iOS.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'dark' ? '#000000' : '#ffffff');
}

/** À appeler une fois au démarrage. */
export function initTheme(): void {
  applyTheme();

  // En mode automatique, suivre les changements du système (bascule nocturne).
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      if (choice !== 'auto') return;

      applyTheme();
      for (const listener of listeners) listener();
    });
}
